import React, { useMemo } from 'react';
import { Link as MuiLink } from '@mui/material';

import {
  TableCell,
  TableRow,
  TableRowActionsMenu,
  StatusChip,
  StatusChipVariant,
} from '@occc-app/design-system';
import { KioskAdSetFragment, Kiosk_Ad_Set_Status } from '@occc/graphql';

import { ThumbnailImage } from '../../components/thumbnail-image';
import { TABLE_CELL_TRUNCATE_STYLES, TABLE_CELL_IMAGE_STYLES } from '../../touchpoint.constants';
import type { OfferLinkInfo } from '../../utils/offer-url';

type AdSetsListTableRowProps = {
  adSet: KioskAdSetFragment;
  offer?: OfferLinkInfo;
  actions: {
    onItemClick: (adSetId: string) => void;
    onView: (adSetId: string) => void;
    onEdit: (adSetId: string) => void;
    onDelete: (adSetId: string) => void;
    onPublish: (adSetId: string) => void;
    onArchive: (adSetId: string) => void;
  };
  permissions: {
    canEdit: boolean;
    canView: boolean;
  };
  isAdminUser: boolean;
};

const getStatusLabel = (status: Kiosk_Ad_Set_Status): string => {
  switch (status) {
    case Kiosk_Ad_Set_Status.Published:
      return 'Published';
    case Kiosk_Ad_Set_Status.Draft:
      return 'Draft';
    case Kiosk_Ad_Set_Status.Archived:
      return 'Archived';
    default:
      return status;
  }
};

const getStatusVariant = (status: Kiosk_Ad_Set_Status): StatusChipVariant => {
  switch (status) {
    case Kiosk_Ad_Set_Status.Published:
      return 'success';
    case Kiosk_Ad_Set_Status.Draft:
      return 'warning';
    case Kiosk_Ad_Set_Status.Archived:
      return 'default';
    default:
      return 'default';
  }
};

const AdSetsListTableRowComponent = ({
  adSet,
  offer,
  actions,
  permissions,
  isAdminUser,
}: AdSetsListTableRowProps) => {
  const { id, name, description, status, asset } = adSet;

  const isDraft = status === Kiosk_Ad_Set_Status.Draft;
  const isPublished = status === Kiosk_Ad_Set_Status.Published;

  const menuItems = useMemo(() => {
    const items = [];

    if (permissions.canView) {
      items.push({
        label: 'View',
        onClick: () => actions.onView(id),
        dataTestId: 'view',
      });
    }

    if (permissions.canEdit) {
      items.push({
        label: 'Edit',
        onClick: () => actions.onEdit(id),
        dataTestId: 'edit',
      });

      if (isDraft) {
        items.push({
          label: 'Publish',
          onClick: () => actions.onPublish(id),
          dataTestId: 'publish',
        });
        items.push({
          label: 'Delete',
          onClick: () => actions.onDelete(id),
          dataTestId: 'delete',
        });
      }

      if (isPublished) {
        items.push({
          label: 'Archive',
          onClick: () => actions.onArchive(id),
          dataTestId: 'archive',
        });
      }
    }

    return items;
  }, [actions, permissions, id, isDraft, isPublished]);

  const onRowClick = () => {
    actions.onItemClick(id);
  };

  return (
    <TableRow onClick={onRowClick}>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>{name || ''}</TableCell>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>{description || ''}</TableCell>
      <TableCell>
        <StatusChip label={getStatusLabel(status)} variant={getStatusVariant(status)} />
      </TableCell>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>
        {offer ? (
          <MuiLink
            href={offer.url}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e) => e.stopPropagation()}
          >
            {offer.name}
          </MuiLink>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>{asset?.name || ''}</TableCell>
      <TableCell sx={TABLE_CELL_IMAGE_STYLES}>
        <ThumbnailImage
          imageUrl={adSet.asset?.imageSet?.square}
          altText={name || 'Ad set thumbnail'}
          size={40}
        />
      </TableCell>
      {isAdminUser && (
        <TableCell padding='button' align='right'>
          <TableRowActionsMenu menuItems={menuItems} />
        </TableCell>
      )}
    </TableRow>
  );
};

export const AdSetsListTableRow = React.memo<AdSetsListTableRowProps>(AdSetsListTableRowComponent);
